/**
 * StaffRosterTimeline.tsx
 * The people×weeks "Team capacity" SPLIT AVAILABILITY BAR grid (redesign
 * 2026-07-14, qa/staff-this-job-redesign-2026-07-14/SPEC.md §2 — matches the
 * approved mockup `capacity-finalized.html`). Two week zones in one
 * continuous table:
 *   - PROJECT WINDOW weeks (the task span) — editable: the split bar +
 *     uniform ± typeable stepper (step 20%, press + on 0% → 100%).
 *   - BEYOND PROJECT weeks (a read-only future horizon) — availability
 *     context only, so a scheduler can see where a busy person frees up.
 * A "Show" toolbar (Committed / Uncommitted / Free) toggles segment
 * visibility; clicking a name or bar opens the person's inline calendar
 * accordion (v1.1.49 — the old breakdown popover is retired from this grid).
 *
 * Fed by the SHARED useStaffJobModel (one roster + one capacity fetch), so
 * the task cards and this grid can never drift. Colors via theme tokens — no
 * hex literals (CODING-STANDARDS).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { alpha, useTheme } from '@mui/material/styles';

import { staffCardSx, staffHair, staffToolbarBg, STAFF_CELL_RADIUS } from './staffViewStyle';
import { InitialsAvatar } from '../../../../components/schedule/InitialsAvatar';
import { EmployeeAccordionPlan, type PinnedPlanRow } from '../../../../components/schedule/EmployeeAccordionPlan';
import { WeekLoadBars } from '../../../../components/schedule/EmployeeWeekLoadRow';
import { addDaysISO } from '../../../../components/schedule/employeeScheduleShared';
import { buildPersonWeekLoads, weekUtilizationPercent, type DayLoad } from '../../../../utils/personDayLoad';
import { plannedFteWeeks } from '../../../../utils/fteDemand';
import { FteDemandStrip } from './FteDemandStrip';
import { deriveProjectNickname } from '../../../../utils/projectNickname';
import { fetchEmployeeBars, type EmployeeBar } from '../../../../services/scheduleEmployeeBars';
import { Phase2RailScrollArrows } from './Phase2RailScrollArrows';
import { RosterWeekCell } from './RosterWeekCell';
import { PercentStepperInput } from './PercentStepperInput';
import { CapacityAddPersonRow } from './CapacityAddPersonRow';
import { CapacityShowFilter } from './CapacityShowFilter';
import { buildWeekHeaders, FUTURE_HORIZON_WEEKS } from './staffCapacityModel';
import { isRemoveFromJobIntent } from './staffCapacityModel';
import { buildPersonWeekTasks, buildRosterCellModel, collectScenarioShareDays, personWeekKey } from './staffRosterTimelineModel';
import { rollupPrepTasks } from './prepRollup';
import { DEFAULT_SPLIT_VISIBILITY, type SplitVisibility } from './capacitySplitModel';
import { fmtShortDate, saturdayWeekStart } from '../../../../utils/weekUtils';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../../components/shared/ConfirmDialog';
import type { UseStaffJobModelResult } from './useStaffJobModel';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

/** Person-name gutter column width. */
const NAME_COL_W = 190;
/** One project-window week column — wide enough for the split bar + the stepper (2026-07-14, +10% twice). */
const WEEK_COL_W = 208;
/** Beyond-project (read-only) week column — narrower, it's just availability context. */
const FUTURE_WEEK_COL_W = 128;
/** ScenarioKey marker for synthesized this-draft bars (never a real GUID). */
const SYNTH_SCENARIO_KEY = '__this-scenario__';
/** P7: weeks added/removed per click of the outlook-horizon pagers. */
const HORIZON_STEP = 4;

export interface StaffRosterTimelineProps {
  model: UseStaffJobModelResult;
  tasks: TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  /** Index-test allocations + their assignees (2026-07-29) — pinned "(index)"/"(effort)" context rows. */
  indexAllocations?: Array<{ IndexAllocationKey: string; TaskTypeName?: string | null; StartDate: string; EndDate: string; Kind?: 'index' | 'effort' }>;
  indexAssigneesByAllocation?: Record<string, Array<{ UserKey: string }>>;
  /**
   * P7 (2026-07-29): the read-only future horizon is extendable — the "Beyond
   * project" pill grows ◀/▶ pagers when the parent owns this state. Both
   * optional so legacy embeds (StaffRosterView) keep the fixed default.
   */
  futureHorizonWeeks?: number;
  onFutureHorizonChange?: (weeks: number) => void;
  editable: boolean;
  hoveredWeek: string | null;
  onHoverWeek: (weekStart: string | null) => void;
  /** The plan source — identifies the "this project" segment in the breakdown popover. */
  source?: 'Quote' | 'WorkOrder' | null;
  sourceKey?: string | null;
  scenarioKey?: string | null;
  /** userKey (lowercased) → resolved MS Graph photo, for the Team column avatars. */
  photoByUserKey?: Map<string, string | null>;
  /**
   * Highlight the whole week column on hover (the shared-rail "beam"). On for the
   * legacy rail views; the standalone Staff-this-job hero turns it OFF — there's
   * no second grid to coordinate with, so the full-column highlight is just noise.
   */
  weekHoverHighlight?: boolean;
}

export const StaffRosterTimeline: React.FC<StaffRosterTimelineProps> = ({
  model,
  tasks,
  assignmentMap,
  indexAllocations = [],
  indexAssigneesByAllocation = {},
  editable,
  hoveredWeek,
  onHoverWeek,
  source = null,
  sourceKey = null,
  photoByUserKey,
  weekHoverHighlight = true,
  futureHorizonWeeks,
  onFutureHorizonChange,
}) => {
  const theme = useTheme();
  const { weekColumns, projectWeekColumns, isProjectWeek, employeeRows, teamTotalByWeek, allocPeakByUser, candidates, existingKeys } = model;
  const beamRail = `2px solid ${theme.schedule.selection}`;
  const futureWeekCount = weekColumns.length - projectWeekColumns.length;

  const weekHeaders = useMemo(() => buildWeekHeaders(weekColumns), [weekColumns]);
  const personWeekTasks = useMemo(() => buildPersonWeekTasks(assignmentMap, tasks), [assignmentMap, tasks]);

  // "Show" segment-visibility toggle (Committed / Uncommitted / Free) — UI-only.
  const [visibility, setVisibility] = useState<SplitVisibility>(DEFAULT_SPLIT_VISIBILITY);

  // Expandable Team gutter (2026-07-26 feedback: 190px truncates task labels) —
  // the ⟷ toggle in the header widens it so the accordion's task list reads fully.
  const [wideTeam, setWideTeam] = useState(false);
  const gutterW = wideTeam ? 340 : NAME_COL_W;

  // Horizontal week paging via floating ◀/▶ arrows (2026-07-14) — the native
  // scrollbar is hidden; these edge buttons page the weeks instead.
  const railScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = railScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);
  useEffect(() => {
    const el = railScrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateArrows); ro.disconnect(); };
  }, [updateArrows, weekColumns.length, employeeRows.length]);
  const scrollByCol = useCallback((dir: -1 | 1) => {
    railScrollRef.current?.scrollBy({ left: dir * (WEEK_COL_W + 4), behavior: 'smooth' });
  }, []);

  // ── S1 accordion (round-13 pick, 2026-07-25): clicking a NAME opens the
  // person's calendar inline under their row — the Employee Schedule's
  // breakdown over the same week columns, plus a pinned slate "This job" row
  // for the draft's own share (the employee-bars payload shows only the LIVE
  // schedule: Committed + Tentative BASELINE — this draft's rows aren't in it).
  // Bar clicks keep the existing popover.
  const [openPlans, setOpenPlans] = useState<Set<string>>(new Set());
  const [windowBars, setWindowBars] = useState<EmployeeBar[] | null>(null);
  const windowStart = weekColumns[0] ?? null;
  const windowEnd = weekColumns.length ? addDaysISO(weekColumns[weekColumns.length - 1], 6) : null;
  useEffect(() => {
    // Fetch lazily — only once someone opens an accordion.
    if (openPlans.size === 0 || windowBars !== null || !windowStart || !windowEnd) return;
    let cancelled = false;
    fetchEmployeeBars(windowStart, windowEnd)
      .then((res) => { if (!cancelled) setWindowBars(res.bars); })
      .catch(() => { if (!cancelled) setWindowBars([]); });
    return () => { cancelled = true; };
  }, [openPlans, windowBars, windowStart, windowEnd]);
  const togglePlan = useCallback((userKey: string) => {
    setOpenPlans((prev) => {
      const next = new Set(prev);
      next.has(userKey) ? next.delete(userKey) : next.add(userKey);
      return next;
    });
  }, []);
  const barsForUser = useCallback((userKey: string): EmployeeBar[] => {
    const lk = userKey.toLowerCase();
    return (windowBars ?? []).filter((b) => b.userKey.toLowerCase() === lk);
  }, [windowBars]);
  const accordionWeekWidths = useMemo(
    () => weekColumns.map((ws) => (isProjectWeek(ws) ? WEEK_COL_W : FUTURE_WEEK_COL_W)),
    [weekColumns, isProjectWeek],
  );
  // One pinned row PER SCENARIO TASK the person is on (v1.1.47 mockup fidelity),
  // spanning the task's real assigned days; % = the week's roster share (the
  // number the steppers edit). Consolidated prep (BUG-2026-0165): the prep
  // steps' member tasks fold into ONE line per step, runs merged across
  // members. Roster-only members (share but no task rows yet) fall back to one
  // generic Mon–Fri "This job" line.
  const pinnedThisJob = useCallback((userKey: string): PinnedPlanRow[] => {
    const lk = userKey.toLowerCase();
    const out: PinnedPlanRow[] = [];
    const buildRuns = (members: TaskWithBookings[], label: string): PinnedPlanRow['runs'] => {
      const mine = members
        .flatMap((t) => assignmentMap[t.ScheduleTaskKey] ?? [])
        .filter((a) => a.UserKey.toLowerCase() === lk)
        .sort((a, b) => a.StartDate.localeCompare(b.StartDate));
      const runs: PinnedPlanRow['runs'] = [];
      for (const a of mine) {
        const pct = model.sourcePercentFor(userKey, a.WeekStart) || a.Percent;
        const prev = runs[runs.length - 1];
        if (prev && addDaysISO(prev.endDate, 1) >= a.StartDate && prev.percent === pct) {
          if (a.EndDate > prev.endDate) prev.endDate = a.EndDate;
        } else if (!(prev && a.EndDate <= prev.endDate && prev.percent === pct)) {
          runs.push({ startDate: a.StartDate, endDate: a.EndDate, percent: pct,
            tooltip: `${label} · ${a.StartDate} – ${a.EndDate} · ${pct}% of their day — tentative until the plan is submitted` });
        }
      }
      return runs;
    };
    for (const entry of rollupPrepTasks(tasks)) {
      const members = entry.kind === 'task' ? [entry.task] : entry.group.members;
      const rep = members[0];
      const label = entry.kind === 'task' ? rep.Name : entry.group.name;
      const runs = buildRuns(members, label);
      if (!runs.length) continue;
      out.push({
        nickname: deriveProjectNickname(rep.WorkOrderName?.trim() || rep.ProjectName?.trim() || rep.QuoteNumber || 'This job'),
        taskName: `${label} — this scenario`,
        color: theme.schedule.machineTime,
        tentative: true,
        runs,
      });
    }
    // Index-test allocations the person is staffed on (2026-07-29): one pinned
    // line per allocation, spanning its window, % = that week's roster share.
    for (const a of indexAllocations) {
      const staffed = (indexAssigneesByAllocation[a.IndexAllocationKey] ?? [])
        .some((x) => x.UserKey.toLowerCase() === lk);
      if (!staffed) continue;
      const pct = model.sourcePercentFor(userKey, saturdayWeekStart(a.StartDate)) || 100;
      const kindLabel = (a.Kind ?? 'index') === 'effort' ? 'effort' : 'index';
      out.push({
        nickname: 'This job',
        taskName: `${a.TaskTypeName ?? 'Index test'} (${kindLabel}) — this scenario`,
        color: theme.schedule.machineTime,
        tentative: true,
        runs: [{
          startDate: a.StartDate,
          endDate: a.EndDate,
          percent: pct,
          tooltip: `${a.TaskTypeName ?? 'Index test'} (${kindLabel}) · ${a.StartDate} – ${a.EndDate} · ${pct}% of their day — tentative until the plan is submitted`,
        }],
      });
    }
    if (out.length) return out;
    const runs = projectWeekColumns
      .map((ws) => ({ ws, pct: model.sourcePercentFor(userKey, ws) }))
      .filter((x) => x.pct > 0)
      .map((x) => ({
        startDate: addDaysISO(x.ws, 2), // Mon
        endDate: addDaysISO(x.ws, 6),   // Fri
        percent: x.pct,
        tooltip: `This job · week of ${fmtShortDate(x.ws)} · ${x.pct}% of their day — tentative until the plan is submitted`,
      }));
    return runs.length ? [{ nickname: 'This job', taskName: 'your tentative share (set with the steppers)', color: theme.schedule.machineTime, tentative: true, runs }] : [];
  }, [tasks, assignmentMap, projectWeekColumns, model, theme.schedule.machineTime, indexAllocations, indexAssigneesByAllocation]);

  // ── Day-load morph (v1.1.47): while a row is open its week cells become the
  // Employee Schedule's 7-day load bars. The draft's own share is synthesized
  // as Tentative bars (it's absent from the live employee-bars payload) — ONE
  // segment per person-week over the union of their task days, so two tasks in
  // the same week never double-count the share.
  const scenarioBarsFor = useCallback((userKey: string): EmployeeBar[] => {
    const out: EmployeeBar[] = [];
    for (const ws of projectWeekColumns) {
      const pct = model.sourcePercentFor(userKey, ws);
      if (pct <= 0) continue;
      // Task days + index/effort allocation window days (BUG 2026-07-30: an
      // index-staffed person with no task rows used to fall through to the
      // Mon–Fri fallback and read "booked all week" for a one-day test).
      // Weekend days are skipped inside the collector — a range spanning a
      // weekend runs the MACHINE, not the person (2026-07-26).
      const sorted = collectScenarioShareDays({
        userKey,
        weekStart: ws,
        tasks,
        assignmentMap,
        indexAllocations,
        indexAssigneesByAllocation,
      });
      let runStart = sorted[0];
      for (let i = 1; i <= sorted.length; i++) {
        const d = sorted[i];
        if (d !== undefined && addDaysISO(sorted[i - 1], 1) === d) continue;
        out.push({
          assignmentKey: `synth-${ws}-${runStart}`, userKey, name: 'This job', kind: 'Task',
          weekStart: ws, startDate: runStart, endDate: sorted[i - 1], percent: pct, rawPercent: pct,
          status: 'Tentative', source: source ?? 'Manual', scenarioKey: SYNTH_SCENARIO_KEY,
          scheduleTaskKey: null, taskTypeKey: null, quoteKey: null, quoteNumber: null,
          workOrderKey: null, workOrderNo: null, workOrderName: null, projectKey: null,
          projectId: null, projectName: null, machineBookingKey: null, machineCode: null,
          dayLocked: false, actingUserSkillLevel: null, conflictType: null, overbooked: false,
        });
        if (d !== undefined) runStart = d;
      }
    }
    return out;
  }, [projectWeekColumns, model, tasks, assignmentMap, source, indexAllocations, indexAssigneesByAllocation]);
  const scenarioAccent = useCallback(
    (day: DayLoad) => (day.parts.some((p) => p.bar.scenarioKey === SYNTH_SCENARIO_KEY) ? theme.schedule.machineTime : null),
    [theme.schedule.machineTime],
  );

  // 0% ⇒ remove-from-job confirm (a present person is always > 0% — dialing to
  // zero means "take them off this job that week", not "present at 0%").
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const closeConfirm = useCallback(() => setConfirm((c) => ({ ...c, open: false })), []);

  const handleSetPercent = useCallback((userKey: string, displayName: string, weekStart: string, percent: number) => {
    if (isRemoveFromJobIntent(percent, model.sourcePercentFor(userKey, weekStart))) {
      setConfirm({
        open: true,
        title: 'Remove from this job?',
        message: `Remove ${displayName} from this job for the week of ${fmtShortDate(weekStart)}? They’ll come off its tasks that week.`,
        confirmLabel: 'Remove',
        confirmColor: 'error',
        onConfirm: () => model.removeMember(userKey, weekStart),
      });
      return;
    }
    model.setAllocation(userKey, displayName, weekStart, percent);
  }, [model]);

  return (
    <Box sx={staffCardSx(theme)}>
      {/* Sage-tinted toolbar strip (mockup `.toolbar`) — the "Show" segment filter.
          The section title now lives in the numbered label above this card. */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: 0.6, flexWrap: 'wrap',
          backgroundColor: staffToolbarBg(theme),
          borderBottom: `1px solid ${staffHair(theme)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mr: 'auto', flexWrap: 'wrap' }}>
          {employeeRows.length === 0 ? (
            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>nobody on the job yet</Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: theme.schedule.machineTime }} />
                <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 600 }}>This project</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.66rem', color: theme.schedule.allocOver, fontWeight: 700 }}>▲ over 100%</Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontStyle: 'italic' }}>Click a name or bar to open their calendar</Typography>
            </>
          )}
        </Box>
        <CapacityShowFilter value={visibility} onChange={setVisibility} />
      </Box>

      {/* Rough-FTE demand vs plan (round-14a) — quote-sourced scenarios only;
          renders nothing when there's no priced quote. */}
      <FteDemandStrip
        source={source}
        sourceKey={sourceKey}
        plannedFteWeeks={plannedFteWeeks(teamTotalByWeek, projectWeekColumns)}
      />

      {employeeRows.length === 0 || weekColumns.length === 0 ? (
        <Typography variant="caption" sx={{ display: 'block', px: 1.25, py: 1.25, fontSize: '0.72rem', color: 'text.disabled' }}>
          No one on the job yet — assign people to the tasks above and they appear here at 100% for the weeks their tasks run.
        </Typography>
      ) : (
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', pb: 0.75, fontSize: '0.68rem', color: 'text.secondary' }}>
            Set each person&rsquo;s share of the week by hand — default 100%, dial it up or down as the week gets heavier or
            lighter (step 20%). The bar splits Committed (firm) / Uncommitted (other drafts) / This project (editable) / Free.
            Over 100% is allowed (advisory). Click a name or bar to open the person&rsquo;s calendar. Weeks beyond this job are read-only context.
          </Typography>

          <Box sx={{ position: 'relative' }}>
          <Box
            ref={railScrollRef}
            onMouseLeave={weekHoverHighlight ? () => onHoverWeek(null) : undefined}
            sx={{
              border: `1px solid ${staffHair(theme)}`,
              borderRadius: `${STAFF_CELL_RADIUS}px`,
              overflowX: 'auto',
              backgroundColor: theme.palette.background.paper,
              width: '100%',
              // Native scrollbar hidden — the floating ◀/▶ arrows page the weeks.
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: gutterW + projectWeekColumns.length * WEEK_COL_W + futureWeekCount * FUTURE_WEEK_COL_W }}>
              {/* Under tableLayout:fixed the browser takes column widths from the FIRST
                  row — the zone header's colSpan cells — and splits the weeks evenly,
                  ignoring the per-cell widths below. The colgroup pins the real widths
                  (208 project / 128 future) so the S1 accordion aligns exactly. */}
              <colgroup>
                <col style={{ width: gutterW }} />
                {weekColumns.map((ws) => (
                  <col key={ws} style={{ width: isProjectWeek(ws) ? WEEK_COL_W : FUTURE_WEEK_COL_W }} />
                ))}
              </colgroup>
              <TableHead>
                {/* Zone header — Project window vs Beyond project (availability outlook). */}
                <TableRow>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: theme.palette.background.paper, width: gutterW, borderBottom: 'none', py: 0.25 }} />
                  <TableCell colSpan={projectWeekColumns.length} sx={{ py: 0.5, px: 0.6, borderBottom: 'none', backgroundColor: 'transparent' }}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: theme.schedule.committed,
                        color: theme.palette.getContrastText(theme.schedule.committed),
                        borderRadius: '999px', py: 0.5, px: 1.5,
                        fontSize: '0.68rem', fontWeight: 800, letterSpacing: 0.3,
                      }}
                    >
                      ◆ Project window
                    </Box>
                  </TableCell>
                  {futureWeekCount > 0 && (
                    <TableCell colSpan={futureWeekCount} sx={{ py: 0.5, px: 0.6, borderBottom: 'none', backgroundColor: 'transparent' }}>
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                          backgroundColor: theme.schedule.selectionBg, color: 'text.secondary',
                          border: `1px dashed ${staffHair(theme)}`, borderRadius: '999px', py: 0.4, px: 1.5,
                          fontSize: '0.68rem', fontWeight: 700,
                        }}
                      >
                        {/* P7 (2026-07-29): ◀ shrink / ▶ extend the outlook horizon
                            (step {HORIZON_STEP} weeks) — only when the parent owns
                            the horizon state. */}
                        {onFutureHorizonChange && (futureHorizonWeeks ?? 0) > FUTURE_HORIZON_WEEKS && (
                          <Tooltip title={`Show ${HORIZON_STEP} fewer weeks`} arrow>
                            <IconButton
                              size="small"
                              onClick={() => onFutureHorizonChange(Math.max(FUTURE_HORIZON_WEEKS, (futureHorizonWeeks ?? FUTURE_HORIZON_WEEKS) - HORIZON_STEP))}
                              sx={{ p: 0, color: 'inherit' }}
                            >
                              <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        Beyond project — availability outlook
                        {onFutureHorizonChange && (
                          <Tooltip title={`Look ${HORIZON_STEP} more weeks ahead`} arrow>
                            <IconButton
                              size="small"
                              onClick={() => onFutureHorizonChange((futureHorizonWeeks ?? FUTURE_HORIZON_WEEKS) + HORIZON_STEP)}
                              sx={{ p: 0, color: 'inherit' }}
                            >
                              <KeyboardDoubleArrowRightIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 3,
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 800, fontSize: '0.78rem', letterSpacing: 0.2,
                      width: gutterW, py: 1, px: 1.5,
                      borderBottom: `2px solid ${staffHair(theme)}`,
                      borderRight: `1px solid ${staffHair(theme)}`,
                      boxShadow: `6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.12)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Team
                      {/* ⟷ widen the gutter so task labels read fully (2026-07-26). */}
                      <Tooltip title={wideTeam ? 'Shrink the Team column' : 'Expand the Team column to read full task names'} arrow>
                        <IconButton size="small" onClick={() => setWideTeam((w) => !w)} sx={{ p: 0.25 }}>
                          {wideTeam
                            ? <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 16 }} />
                            : <KeyboardDoubleArrowRightIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {weekHeaders.map((wh) => {
                    const hot = weekHoverHighlight && hoveredWeek === wh.weekStart;
                    const proj = isProjectWeek(wh.weekStart);
                    return (
                      <TableCell
                        key={wh.weekStart}
                        align="center"
                        onMouseEnter={weekHoverHighlight ? () => onHoverWeek(wh.weekStart) : undefined}
                        sx={{
                          // Project weeks carry a green tint + ◆ (v1.1.47 mockup fidelity).
                          backgroundColor: hot ? alpha(theme.schedule.selection, 0.14)
                            : proj ? alpha(theme.schedule.committed, 0.08)
                            : theme.palette.background.paper,
                          py: 0.6, px: 0.5, width: proj ? WEEK_COL_W : FUTURE_WEEK_COL_W,
                          borderBottom: proj ? `2px solid ${theme.schedule.committed}` : `2px solid ${staffHair(theme)}`,
                          borderRight: hot ? beamRail : `1px solid ${staffHair(theme)}`,
                          ...(hot ? { borderLeft: beamRail } : {}),
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography variant="caption" display="block" fontWeight={700} sx={{ fontSize: '0.68rem', letterSpacing: 0.5, color: proj ? theme.schedule.committed : 'text.secondary' }}>
                          {wh.numberLabel}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: proj ? theme.schedule.committed : 'text.disabled' }}>
                          {wh.rangeLabel}{proj ? ' ◆' : ''}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {employeeRows.map((emp, rowIdx) => {
                  const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : undefined;
                  const planOpen = openPlans.has(emp.userKey);
                  // Morph the open row's week cells into the 7-day load bars
                  // (live plan + this draft's synthesized share).
                  const morphed = planOpen && windowBars !== null;
                  const openAggs = morphed
                    ? buildPersonWeekLoads([...barsForUser(emp.userKey), ...scenarioBarsFor(emp.userKey)], weekColumns)
                    : null;
                  return (
                    <React.Fragment key={emp.userKey}>
                    <TableRow data-flash-user={emp.userKey} sx={{ backgroundColor: rowBg }}>
                      <TableCell
                        sx={{
                          position: 'sticky', left: 0, zIndex: 1,
                          backgroundColor: rowBg ?? theme.palette.background.paper,
                          borderRight: `1px solid ${staffHair(theme)}`,
                          boxShadow: `6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                          py: 0.75, px: 1.5, width: gutterW, verticalAlign: 'middle',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                          <InitialsAvatar
                            displayName={emp.displayName}
                            seed={emp.userKey}
                            photoUrl={photoByUserKey?.get(emp.userKey.toLowerCase()) ?? null}
                            size={28}
                          />
                          {/* S1 (2026-07-25): name click opens the inline calendar
                              accordion (v1.1.49: bar clicks do too — popover retired). */}
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-expanded={planOpen}
                            onClick={() => togglePlan(emp.userKey)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlan(emp.userKey); } }}
                            title={planOpen ? 'Hide their calendar' : 'Show their calendar for these weeks'}
                            sx={{ flex: 1, minWidth: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: `${STAFF_CELL_RADIUS}px`, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) } }}
                          >
                            <Typography component="span" sx={{ fontSize: '0.55rem', color: 'text.secondary', transition: 'transform 200ms', transform: planOpen ? 'rotate(90deg)' : 'none', flexShrink: 0 }}>▶</Typography>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem' }}>
                                {emp.displayName}
                              </Typography>
                              {emp.role && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
                                  {emp.role}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {editable && (
                            <Tooltip title={`Remove ${emp.displayName} from this job`} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Remove ${emp.displayName}`}
                                  onClick={() => setConfirm({
                                    open: true,
                                    title: 'Remove from this job?',
                                    message: `Remove ${emp.displayName} from this job? They’ll be taken off all its tasks for every week in the project window.`,
                                    confirmLabel: 'Remove',
                                    confirmColor: 'error',
                                    // Full removal (2026-07-14 bug fix): the old handler only cleared the
                                    // roster % for week 1, and never reloaded the task assignees — so the
                                    // person lingered in the grid until F5. Now we (a) clear the roster
                                    // share for every project week that has one, AND (b) unassign them
                                    // from every task they're on via removeFromTask, which reloads that
                                    // task's assignees so the row drops immediately.
                                    onConfirm: () => {
                                      for (const ws of projectWeekColumns) {
                                        // Ghost-row fix (2026-07-28): also clear weeks whose roster row
                                        // sits at 0% (leftover allocation) — percent>0 alone made the ×
                                        // a silent no-op on such rows.
                                        if (model.sourcePercentFor(emp.userKey, ws) > 0 || model.hasRosterEntry(emp.userKey, ws)) {
                                          model.removeMember(emp.userKey, ws);
                                        }
                                      }
                                      const lk = emp.userKey.toLowerCase();
                                      for (const t of tasks) {
                                        if ((assignmentMap[t.ScheduleTaskKey] ?? []).some((a) => a.UserKey.toLowerCase() === lk)) {
                                          model.removeFromTask(t.ScheduleTaskKey, emp.userKey);
                                        }
                                      }
                                    },
                                  })}
                                  sx={{ p: 0.25 }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {weekColumns.map((ws) => {
                        const hot = weekHoverHighlight && hoveredWeek === ws;
                        const proj = isProjectWeek(ws);
                        const cell = model.cellFor(emp.userKey, ws);
                        const thisJobPercent = proj ? model.sourcePercentFor(emp.userKey, ws) : (cell?.thisSourcePercent ?? 0);
                        const cellModel = buildRosterCellModel({
                          thisJobPercent,
                          totalPercent: cell?.allocationPercent,
                          tasks: personWeekTasks.get(personWeekKey(emp.userKey, ws)) ?? [],
                          committedPercent: cell?.committedPercent,
                          // BUG-2026-0175: "Uncommitted" = tentative-other within
                          // this overlay PLUS other DRAFT plans' tentative holds
                          // (the additive otherDraftTentativePercent feed).
                          uncommittedPercent:
                            (cell?.tentativeOtherPercent ?? 0) + (cell?.otherDraftTentativePercent ?? 0),
                        });
                        return (
                          <TableCell
                            key={ws}
                            align="center"
                            onMouseEnter={weekHoverHighlight ? () => onHoverWeek(ws) : undefined}
                            sx={{
                              width: proj ? WEEK_COL_W : FUTURE_WEEK_COL_W, px: 0, py: 0.25,
                              backgroundColor: hot ? alpha(theme.schedule.selection, 0.06) : undefined,
                              borderBottom: `1px solid ${staffHair(theme)}`,
                              borderRight: hot ? beamRail : `1px solid ${staffHair(theme)}`,
                              ...(hot ? { borderLeft: beamRail } : {}),
                              verticalAlign: morphed ? 'middle' : 'top',
                            }}
                          >
                            {morphed && openAggs ? (
                              // Open row (v1.1.47): the 7-day skyline — slate on days
                              // carrying this draft's share, signal colours elsewhere.
                              // Clicking the bars collapses the calendar again
                              // (v1.1.49 — the popover is retired). The ± stepper
                              // STAYS under the skyline in editable project weeks
                              // (2026-07-26 — editing shouldn't require collapsing).
                              // %-line = intensity-aware utilization (avg daily
                              // load), NOT days-touched — a 25% mentor across 4 of
                              // 5 days reads 20%, not 80%.
                              <>
                                <Box
                                  onClick={() => togglePlan(emp.userKey)}
                                  title="Hide their calendar"
                                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', px: 0.75, py: 0.25, cursor: 'pointer' }}
                                >
                                  <WeekLoadBars
                                    agg={openAggs[weekColumns.indexOf(ws)]}
                                    accentFor={scenarioAccent}
                                    pctSuffix=" of week booked"
                                    pctValue={weekUtilizationPercent(openAggs[weekColumns.indexOf(ws)].days)}
                                  />
                                </Box>
                                {editable && proj && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', pb: 0.5 }}>
                                    <PercentStepperInput
                                      value={thisJobPercent}
                                      onChange={(p) => handleSetPercent(emp.userKey, emp.displayName, ws, p)}
                                    />
                                  </Box>
                                )}
                              </>
                            ) : (
                              <RosterWeekCell
                                model={cellModel}
                                editable={editable && proj}
                                isFutureWeek={!proj}
                                hasConflict={cell?.hasOverbook ?? false}
                                visibility={visibility}
                                onSetPercent={(p) => handleSetPercent(emp.userKey, emp.displayName, ws, p)}
                                onOpenDetail={() => togglePlan(emp.userKey)}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {/* S1 accordion — the person's calendar over these exact
                        columns: pinned slate "This job" + their live plan. */}
                    {planOpen && (
                      <TableRow>
                        <TableCell colSpan={1 + weekColumns.length} sx={{ p: 0, borderBottom: `1px solid ${staffHair(theme)}` }}>
                          {windowBars === null ? (
                            <Typography sx={{ px: 2, py: 1, fontSize: '0.7rem', color: 'text.secondary' }}>Loading their calendar…</Typography>
                          ) : (
                            <EmployeeAccordionPlan
                              bars={barsForUser(emp.userKey)}
                              weekStarts={weekColumns}
                              nameWidth={gutterW}
                              weekWidths={accordionWeekWidths}
                              pinnedRows={pinnedThisJob(emp.userKey)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}

                {/* Per-week team total (this job's Σ allocation %) — project weeks only. */}
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 1,
                      backgroundColor: theme.table.headerBg,
                      boxShadow: `6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                      borderTop: `2px solid ${staffHair(theme)}`,
                      py: 0.5, px: 1.5, width: gutterW,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                      Team need (people-time)
                    </Typography>
                  </TableCell>
                  {projectWeekColumns.map((ws) => {
                    const total = Math.round(teamTotalByWeek.get(ws) ?? 0);
                    const hot = weekHoverHighlight && hoveredWeek === ws;
                    // The Σ of everyone's share IS the week's staffing demand in
                    // FTE (100% = one person full-time — the Jul-23 meeting's
                    // "how many full-time equivalents do we need"). It's a
                    // DEMAND figure, so >100% is normal (≥2 people), never red.
                    const fte = total / 100;
                    return (
                      <TableCell
                        key={ws}
                        align="center"
                        onMouseEnter={weekHoverHighlight ? () => onHoverWeek(ws) : undefined}
                        title={`This week the plan uses ${total}% of one person's time in total — about ${fte.toFixed(1)} people (FTE). 100% = one person full-time.`}
                        sx={{
                          width: WEEK_COL_W, py: 0.5,
                          borderTop: `2px solid ${staffHair(theme)}`,
                          backgroundColor: hot ? alpha(theme.schedule.selection, 0.06) : theme.table.headerBg,
                          borderRight: hot ? beamRail : `1px solid ${staffHair(theme)}`,
                          ...(hot ? { borderLeft: beamRail } : {}),
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.74rem', color: 'text.primary' }}>
                          ≈{fte.toFixed(1)} FTE
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ fontSize: '0.64rem', color: 'text.secondary', ml: 0.5 }}>
                          ({total}%)
                        </Typography>
                      </TableCell>
                    );
                  })}
                  {futureWeekCount > 0 && (
                    <TableCell colSpan={futureWeekCount} sx={{ borderTop: `2px solid ${staffHair(theme)}`, backgroundColor: theme.table.headerBg }} />
                  )}
                </TableRow>

                {editable && (
                  <CapacityAddPersonRow
                    candidates={candidates}
                    existingKeys={existingKeys}
                    allocByUser={allocPeakByUser}
                    weekColSpan={projectWeekColumns.length}
                    gutterColSpan={1}
                    gutterWidth={gutterW}
                    onAdd={(userKey, displayName, percent) => {
                      for (const ws of projectWeekColumns) model.setAllocation(userKey, displayName, ws, percent);
                      // The roster add materializes presence on the job's tasks — refresh the
                      // top task table so the person registers (and any "Needs mentor" nag they
                      // resolve clears). Best-effort: the roster PUT is debounced, so wait a beat.
                      setTimeout(() => model.reloadAllTaskAssignees(), 900);
                    }}
                  />
                )}
              </TableBody>
            </Table>
          </Box>
          {/* Floating ◀/▶ week-scroll arrows (native scrollbar hidden). */}
          <Phase2RailScrollArrows canScrollLeft={canScrollLeft} canScrollRight={canScrollRight} onScroll={scrollByCol} />
          </Box>
        </Box>
      )}

      <ConfirmDialog state={confirm} onClose={closeConfirm} />
    </Box>
  );
};
